<template>
	<v-sheet rounded class="mb-4">
		<v-card color="#125B92" elevation="0" class="rounded-b-0">
			<v-card-title class="align-center">
				<h3 class="white--text">{{ client.name }}</h3>
				<v-spacer />
			</v-card-title>
			<v-divider />
			<v-card-text class="white">
				<v-row>
					<div class="paid-chart client-paid-chart w-100">
						<div class="paid-body">
							<div v-if="! widgetsLoadedOnce" class="chart-loading">
								<v-overlay absolute opacity="0.75" color="#fff">
									<v-progress-circular indeterminate size="32" color="#125b92"></v-progress-circular>
								</v-overlay>
							</div>
							<apexchart
								v-else
								type="bar"
								height="320"
								:options="invoiceChartOptions"
								:series="invoiceChartSeries" />
						</div>
					</div>
				</v-row>
				<v-row>
					<v-col cols="12" sm="8">
						<v-card outlined>
							<v-card-text>
								<v-row>
									<v-col cols="12" sm="6">
										<v-card
											outlined
											color="secondary"
											dark
											title="Click to re-calculate"
											@click="getWidgets(true)">
											<v-overlay v-model="widgetsLoading" absolute>
												<v-progress-circular indeterminate size="32"></v-progress-circular>
											</v-overlay>

											<v-card-title class="text-h5 font-mono mb-1">
												{{ widgets.ytd_total | money }}
											</v-card-title>
											<v-card-subtitle><span class="text-uppercase">Total Invoiced - {{ selectedYear }} {{ selectedQuarter ? 'Q' + selectedQuarter : '' }}</span></v-card-subtitle>
										</v-card>
									</v-col>
									<v-col cols="12" sm="6">
										<v-card
											outlined
											color="success"
											dark
											title="Click to re-calculate"
											@click="getWidgets(true)">
											<v-overlay v-model="widgetsLoading" absolute>
												<v-progress-circular indeterminate size="32"></v-progress-circular>
											</v-overlay>

											<v-card-title class="text-h5 font-mono mb-1">
												{{ widgets.ytd_paid | money }}
											</v-card-title>

											<v-card-subtitle><span class="text-uppercase">Total Paid - {{ selectedYear }} {{ selectedQuarter ? 'Q' + selectedQuarter : '' }}</span></v-card-subtitle>
										</v-card>
									</v-col>
								</v-row>
							</v-card-text>
							<template v-if="length(years)">
								<v-card-text>
									<div class="d-flex">
										<v-select
											v-model="selectedYear"
											:items="years"
											outlined
											hide-details
											dense
											class="cursor-pointer mr-3"
											background-color="white"
											label="Revenue Stats by Year"
											@input="getWidgets()" />
										<v-select
											v-model="selectedQuarter"
											:items="selectableQuarters"
											outlined
											hide-details
											dense
											class="cursor-pointer"
											background-color="white"
											label="Filter By Quarter"
											@input="getWidgets()" />
									</div>
								</v-card-text>
							</template>
						</v-card>
					</v-col>
					<v-col cols="12" sm="4">
						<v-card
							outlined
							color="orange darken-2"
							dark
							@click="$emit('outstanding')">
							<v-overlay v-if="!widgetsLoadedOnce" v-model="widgetsLoading" absolute>
								<v-progress-circular indeterminate size="32"></v-progress-circular>
							</v-overlay>

							<v-card-title class="text-h5 font-mono mb-1">
								{{ widgets.total_outstanding | money }}
							</v-card-title>

							<v-card-subtitle><span class="text-uppercase">Total Outstanding - All Time</span></v-card-subtitle>
							<v-divider />
							<v-card-actions class="pa-1 justify-end">
								<v-btn text small @click="$emit('outstanding')">
									View Invoices
								</v-btn>
							</v-card-actions>
						</v-card>
						<div class="mt-2">
							<v-alert outlined color="grey lighten-2" class="text-center">
								<p class="grey--text"><em>Client since {{ client.created_at | localDate }}</em></p>
							</v-alert>
						</div>
					</v-col>
				</v-row>
			</v-card-text>
			<v-card-text>
				<v-row>
					<v-col>
						<v-card v-if="length(widgets.invoiced_job_types) || widgetsLoading" class="card-overflow-hidden" outlined>
							<v-overlay v-if="!widgetsLoadedOnce" v-model="widgetsLoading" absolute>
								<v-progress-circular indeterminate size="32"></v-progress-circular>
							</v-overlay>

							<v-card-title @click="getWidgets(true)">
								Invoiced Job Types
							</v-card-title>

							<v-divider class="thick" />

							<v-list v-if="length(widgets.project_job_types)" dense>
								<template v-for="(amount, type, index) in widgets.invoiced_job_types">
									<v-list-item :key="type" dense>
										<v-list-item-content>
											<span v-if="type" class="text--secondary">{{ type }}</span>
											<em v-else>Other</em>
										</v-list-item-content>
										<v-list-item-action class="text-right justify-end">
											<v-chip class="text--primary" small>{{ amount | numeric }}</v-chip>
										</v-list-item-action>
									</v-list-item>

									<v-divider v-if="index + 1 !== length(widgets.invoiced_job_types)" :key="type + '-divider'" />
								</template>
							</v-list>
						</v-card>
					</v-col>
					<v-col>
						<v-card v-if="length(widgets.project_job_types) || widgetsLoading" class="card-overflow-hidden" outlined>
							<v-overlay v-if="!widgetsLoadedOnce" v-model="widgetsLoading" absolute>
								<v-progress-circular indeterminate size="32"></v-progress-circular>
							</v-overlay>

							<v-card-title @click="getWidgets(true)">
								Project Job Types
							</v-card-title>

							<v-divider class="thick" />

							<v-list v-if="length(widgets.project_job_types)" dense>
								<template v-for="(amount, type, index) in widgets.project_job_types">
									<v-list-item :key="type" dense>
										<v-list-item-content>
											<span v-if="type" class="text--secondary">{{ type }}</span>
											<em v-else>Other</em>
										</v-list-item-content>
										<v-list-item-action class="text-right justify-end">
											<v-chip class="text--primary" small>{{ amount | numeric }}</v-chip>
										</v-list-item-action>
									</v-list-item>

									<v-divider v-if="index + 1 !== length(widgets.project_job_types)" :key="type + '-divider'" />
								</template>
							</v-list>
						</v-card>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
	</v-sheet>
</template>
<script>

import api from '@/api'

export default {
	props: {
		client: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			auth: this.$page.props.auth,
			widgetsLoading: true,
			widgetsLoadedOnce: false,
			widgets: {
				total_invoiced: 0,
				total_paid: 0,
				total_outstanding: 0,
				invoiced_job_types: {},
				project_job_types: {}
			},
			years: [],
			selectedYear: null,
			selectedQuarter: null,
			selectableQuarters: [
				{ value: null, text: 'All Year' },
				{ value: 1, text: 'Q1' },
				{ value: 2, text: 'Q2' },
				{ value: 3, text: 'Q3' },
				{ value: 4, text: 'Q4' }
			],
			invoiceChartData: [],
			invoiceChartOptions: {
				chart: {
					background: '#ffffff',
					cursor: 'default',
					toolbar: {
						show: false
					}
				},
				colors: ['#13799d', '#4e843d'],
				legend: {
					show: true
				},
				dataLabels: {
					enabled: false
				},
				grid: {
					borderColor: '#e5e7eb',
					padding: {
						left: 0,
						right: -38
					}
				},
				plotOptions: {
					bar: {
						columnWidth: '55%'
					}
				},
				tooltip: {
					y: {
						formatter: (amount) => new Intl.NumberFormat('en-US', {
							style: 'currency',
							currency: 'USD'
						}).format(amount)
					}
				},
				xaxis: {
					categories: [],
					labels: {
						rotate: 0
					}
				},
				yaxis: {
					labels: {
						formatter: (amount) => new Intl.NumberFormat('en-US', {
							style: 'currency',
							currency: 'USD',
							maximumFractionDigits: 0
						}).format(amount)
					}
				}
			}
		}
	},
	computed: {
		invoiceChartSeries () {
			return [
				{
					name: `Total Invoiced (${this.selectedYear - 1})`,
					data: this.invoiceChartData.map(({ previous }) => previous)
				},
				{
					name: `Total Invoiced (${this.selectedYear})`,
					data: this.invoiceChartData.map(({ current }) => current)
				}
			]
		}
	},
	mounted () {
		this.getWidgets()
	},
	methods: {
		getWidgets (reset = false) {
			this.widgetsLoading = true

			const method = 'GET'
			const data = {
				client: this.client.id,
				reset: reset === true,
				year: this.selectedYear,
				quarter: this.selectedQuarter
			}

			const url = this.route('clients.api.widgets', data)

			return api({
				method,
				url
			}).then(response => {
				this.widgets = response.data.widgets

				this.invoiceChartData = response.data.invoiceChartData
				this.invoiceChartOptions.xaxis.categories = this.invoiceChartData.map(({ month }) => {
					return new Intl.DateTimeFormat('en-US', {
						month: 'long'
					}).format(new Date(2000, month - 1))
				})

				if (response.data.widgets.hasOwnProperty('years')) {
					this.years = response.data.widgets.years

					if(this.length(this.years) > 0 && ! this.selectedYear) {
						this.selectedYear = this.years[0]

						// Query again if the first pass didn't include a year
						this.getWidgets()
					}
				}

				this.widgetsLoading = false
				this.widgetsLoadedOnce = true
			})
		}
	}
}
</script>
